/**
 * Helper formatting functions for sensor data
 * Safe handling of different data types (numbers, strings, null, etc.)
 */

/**
 * Safely format numbers in scientific notation.
 * Default 4 decimals matches the calibration-constants convention used on the
 * test certificate so UI and PDF render identically (e.g. "8.2230E-3").
 * @param val - Value to format (can be number, string, or any type)
 * @param decimals - Number of decimal places (default: 4)
 * @returns Formatted string in scientific notation or "N/A" if invalid
 */
export const formatScientific = (val: any, decimals: number = 4): string => {
  if (val === null || val === undefined) return "N/A";
  const num = typeof val === 'number' ? val : parseFloat(val);
  return !isNaN(num) ? num.toExponential(decimals).toUpperCase() : String(val);
};

/**
 * Safely format numbers with fixed decimal places
 * @param val - Value to format (can be number, string, or any type)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with fixed decimals or "N/A" if invalid
 */
export const formatFixed = (val: any, decimals: number = 2): string => {
  if (val === null || val === undefined) return "N/A";
  const num = typeof val === 'number' ? val : parseFloat(val);
  return !isNaN(num) ? num.toFixed(decimals) : String(val);
};

/**
 * Safely format percentages
 * @param val - Value to format (can be number, string, or any type)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string as percentage or "N/A" if invalid
 */
export const formatPercent = (val: any, decimals: number = 2): string => {
  if (val === null || val === undefined) return "N/A";
  const num = typeof val === 'number' ? val : parseFloat(val);
  return !isNaN(num) ? `${num.toFixed(decimals)}%` : String(val);
};

// Display precision conventions for calibration values.
// Gauge factor: engineering/scientific notation (4 decimals, capital E) so the UI
//   matches the test certificate exactly (e.g. "8.2230E-3"). The "<unit>/digit"
//   suffix is supplied by the display field's `unit`. See get_linear_g() in the backend.
// Temperature coefficients: 4 decimals.
// Non-linearity, accuracy, hysteresis, repeatability (%FS): 2 decimals.
// Digit values: 1 decimal.
// Pressure: MPa 3 decimals, kPa 1 decimal.
// Displacement: mm 2 decimals.
export const formatGaugeFactor = (val: any) => formatScientific(val, 4);
export const formatTempCoeff = (val: any) => formatFixed(val, 4);
export const formatPercentFS = (val: any) => formatFixed(val, 2);
export const formatDigit = (val: any) => formatFixed(val, 1);
export const formatPressureMPa = (val: any) => formatFixed(val, 3);
export const formatPressureKPa = (val: any) => formatFixed(val, 1);
export const formatDisplacementMm = (val: any) => formatFixed(val, 2);

/**
 * Format stored pressure for display using decimals for the sensor's unit only
 * (no unit conversion). Aligns with certificate Stage 2 behavior.
 */
export const formatPressureByUnit = (val: any, unit?: string | null): string => {
  const u = (unit || "MPa").trim().toLowerCase();
  if (u === "kpa") return formatPressureKPa(val);
  if (u === "mpa") return formatPressureMPa(val);
  return formatFixed(val, 3);
};

